import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useUpdateCurriculum, useCurriculumCourseAssignments, useAssignCoursesToCurriculum } from "@/hooks/useCurricula";
import { useInstructorCourses } from "@/hooks/useCourses";
import { Curriculum } from "@/hooks/useCurricula";
import { useCurriculumAudienceSettings, useUpdateCurriculumAudiences } from "@/hooks/useCurriculumAudienceManager";
import { AudienceRole } from "@/hooks/useCurriculumEligibility";
import { ImagePicker } from "@/components/instructor/ImagePicker";
import { CompletionBadgeSelect } from "@/components/badges/CompletionBadgeSelect";
import { setContentBadge, useCanSetContentBadge, invalidateBadgeQueries } from "@/hooks/useBadges";
import { useHasRole } from "@/hooks/useUserRoles";
import { useAuth } from "@/lib/auth";
import { useQueryClient } from "@tanstack/react-query";
import { BookOpen, AlertCircle, Users } from "lucide-react";

interface EditCurriculumDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  curriculum: Curriculum | null;
}

const AUDIENCE_OPTIONS: { value: AudienceRole; label: string }[] = [
  { value: "entrepreneur", label: "Entrepreneurs" },
  { value: "mentor", label: "Mentors" },
  { value: "admin", label: "Admins" },
  { value: "instructor", label: "Instructors" },
];

export function EditCurriculumDialog({ open, onOpenChange, curriculum }: EditCurriculumDialogProps) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [selectedAudiences, setSelectedAudiences] = useState<AudienceRole[]>([]);
  const [badgeId, setBadgeId] = useState<string | null>(null);
  const { user } = useAuth();
  const isAdmin = useHasRole("admin");
  const queryClient = useQueryClient();
  const { data: canSetBadge } = useCanSetContentBadge("curriculum", curriculum?.id, user?.id, isAdmin);

  const updateCurriculumMutation = useUpdateCurriculum();
  const assignCoursesMutation = useAssignCoursesToCurriculum();
  const updateAudiencesMutation = useUpdateCurriculumAudiences();
  const { data: courses, isLoading: coursesLoading } = useInstructorCourses();
  const { data: currentAssignments, isLoading: assignmentsLoading } = useCurriculumCourseAssignments(curriculum?.id || "");
  const { data: currentAudiences, isLoading: audiencesLoading } = useCurriculumAudienceSettings(curriculum?.id || "");

  useEffect(() => {
    if (curriculum) {
      setTitle(curriculum.title);
      setDescription(curriculum.description || "");
      setThumbnailUrl(curriculum.thumbnail_url || null);
      setBadgeId((curriculum as { badge_id?: string | null }).badge_id ?? null);
    }
  }, [curriculum]);

  useEffect(() => {
    if (currentAssignments) {
      setSelectedCourseIds(currentAssignments.map(a => a.course_id));
    }
  }, [currentAssignments]);

  useEffect(() => {
    if (currentAudiences) {
      setSelectedAudiences(currentAudiences);
    }
  }, [currentAudiences]);

  const handleCourseToggle = (courseId: string, checked: boolean) => {
    if (checked) {
      setSelectedCourseIds(prev => [...prev, courseId]);
    } else {
      setSelectedCourseIds(prev => prev.filter(id => id !== courseId));
    }
  };

  const handleAudienceToggle = (role: AudienceRole, checked: boolean) => {
    if (checked) {
      setSelectedAudiences(prev => [...prev, role]);
    } else {
      setSelectedAudiences(prev => prev.filter(r => r !== role));
    }
  };

  const handleSave = async () => {
    if (!curriculum) return;

    try {
      await updateCurriculumMutation.mutateAsync({
        id: curriculum.id,
        title,
        description: description || null,
        thumbnail_url: thumbnailUrl,
      });

      await assignCoursesMutation.mutateAsync({
        curriculumId: curriculum.id,
        courseIds: selectedCourseIds,
      });

      await updateAudiencesMutation.mutateAsync({
        curriculumId: curriculum.id,
        audiences: selectedAudiences,
      });

      const currentBadge = (curriculum as { badge_id?: string | null }).badge_id ?? null;
      if (canSetBadge && badgeId !== currentBadge) {
        await setContentBadge("curriculum", curriculum.id, badgeId);
        invalidateBadgeQueries(queryClient);
        queryClient.invalidateQueries({ queryKey: ["curricula"] });
        queryClient.invalidateQueries({ queryKey: ["instructor_curricula"] });
      }

      toast({
        title: "Curriculum updated",
        description: "Changes saved successfully.",
      });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update curriculum.",
        variant: "destructive",
      });
    }
  };

  const isLoading = coursesLoading || assignmentsLoading || audiencesLoading;
  const isSaving = updateCurriculumMutation.isPending || assignCoursesMutation.isPending || updateAudiencesMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Curriculum</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="edit-title">Title</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Curriculum title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this curriculum"
              rows={3}
            />
          </div>

          <ImagePicker
            value={thumbnailUrl}
            onChange={setThumbnailUrl}
            label="Curriculum Featured Image"
          />

          {canSetBadge && <CompletionBadgeSelect value={badgeId} onChange={setBadgeId} />}

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Audience & Availability
            </Label>
            <p className="text-xs text-muted-foreground">
              Select which user roles can see and enroll in this curriculum.
            </p>
            {selectedAudiences.length === 0 && (
              <div className="text-xs text-destructive flex items-center gap-1 mt-1">
                <AlertCircle className="h-3 w-3" />
                No audience selected. Enrollment will be blocked.
              </div>
            )}
            <div className="flex flex-wrap gap-4 p-3 border rounded-md">
              {AUDIENCE_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedAudiences.includes(option.value)}
                    onCheckedChange={(checked) => handleAudienceToggle(option.value, !!checked)}
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assign Courses</Label>
            <p className="text-xs text-muted-foreground">
              Select courses to include in this curriculum. Courses can belong to up to 5 curricula.
            </p>
            {isLoading ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Loading courses...</div>
            ) : courses && courses.length > 0 ? (
              <ScrollArea className="h-48 border rounded-md p-2">
                <div className="space-y-2">
                  {courses.map((course) => {
                    const isSelected = selectedCourseIds.includes(course.id);
                    return (
                      <div
                        key={course.id}
                        className="flex items-center gap-3 p-2 rounded hover:bg-muted/50"
                      >
                        <Checkbox
                          id={`course-${course.id}`}
                          checked={isSelected}
                          onCheckedChange={(checked) => handleCourseToggle(course.id, !!checked)}
                        />
                        <label
                          htmlFor={`course-${course.id}`}
                          className="flex-1 text-sm cursor-pointer flex items-center gap-2"
                        >
                          <BookOpen className="h-4 w-4 text-muted-foreground" />
                          <span className="line-clamp-1">{course.title}</span>
                          {!course.is_published && (
                            <Badge variant="secondary" className="text-xs">Draft</Badge>
                          )}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-sm text-muted-foreground py-4 text-center border rounded-md">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                No courses available. Create a course first.
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {selectedCourseIds.length} course{selectedCourseIds.length !== 1 ? "s" : ""} selected
            </p>
          </div>

          <Button
            onClick={handleSave}
            disabled={!title.trim() || isSaving}
            className="w-full"
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
